import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { badges, contributionData } from "../../mocks/dashboard";
import { currentUser } from "../../mocks/dashboard";
import Navbar from "../../components/feature/Navbar";
import GrowPointsPanel from "./components/GrowPointsPanel";
import SwipeChallengeSection from "./components/SwipeChallengeSection";
import LearningStatsSection from "./components/LearningStatsSection";
import WeeklyGoalSection from "./components/WeeklyGoalSection";
import WeaknessAnalysisSection from "./components/WeaknessAnalysisSection";
import ReviewCycleSection from "./components/ReviewCycleSection";
import { useGrowStore } from "../../hooks/useGrowStore";
import ACEBalanceSection from "./components/ACEBalanceSection";
import ValueTrackerSection from "./components/ValueTrackerSection";

// ─── Extra badges from store ─────────────────────────────────────────
const STORE_BADGE_MAP: Record<string, { icon: string; name: string }> = {
  "badge-first": { icon: "🎓", name: "첫 졸업" },
  "badge-3class": { icon: "📚", name: "열공중" },
  "badge-expert": { icon: "👑", name: "전문가 인증" },
  "badge-highscore": { icon: "⭐", name: "우등생" },
};

// ─── Contribution Graph ───────────────────────────────────────────────
function ContributionGraph() {
  const weeks = ["일", "월", "화", "수", "목", "금", "토"];
  const intensity = ["bg-[#F2F4F6]", "bg-[#00D873]/20", "bg-[#00D873]/50", "bg-[#00D873]"];
  return (
    <div>
      <div className="flex gap-1 mb-2">
        {weeks.map((d) => (
          <div key={d} className="flex-1 text-center text-[10px] text-[#191F28]/30">{d}</div>
        ))}
      </div>
      <div className="space-y-1">
        {contributionData.map((row, ri) => (
          <div key={ri} className="flex gap-1">
            {row.map((val, ci) => (
              <div
                key={ci}
                title={val > 0 ? `${val}개 달성` : "미참여"}
                className={`flex-1 rounded-sm ${intensity[val]} transition-all duration-200 hover:scale-110 cursor-pointer`}
                style={{ aspectRatio: "1" }}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <span className="text-xs text-[#191F28]/40">적음</span>
        {intensity.map((c, i) => (<div key={i} className={`w-3 h-3 rounded-sm ${c}`} />))}
        <span className="text-xs text-[#191F28]/40">많음</span>
      </div>
    </div>
  );
}

// ─── Recent Earned Toast ─────────────────────────────────────────────
function EarnedToast({ points, badge, onClose }: { points: number; badge: string | null; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-[#191F28] text-white px-6 py-4 rounded-2xl flex items-center gap-4 animate-fade-in-up" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FFE566] to-[#FFA500] flex items-center justify-center flex-shrink-0">
        <span className="text-lg font-black text-white">₩</span>
      </div>
      <div>
        <p className="text-sm font-extrabold">오늘의 업무 지시 완료! 시드머니 {points}원이 입금되었세오 🎉</p>
        {badge && <p className="text-xs text-[#00D873] mt-0.5">새 배지 획득: {badge}</p>}
      </div>
      <button onClick={onClose} className="ml-2 w-6 h-6 flex items-center justify-center text-white/40 hover:text-white cursor-pointer">
        <i className="ri-close-line" />
      </button>
    </div>
  );
}

// ─── Main Dashboard ──────────────────────────────────────────────────
export default function Dashboard() {
  const [completedIds, setCompletedIds] = useState<number[]>([]);
  const [skippedIds, setSkippedIds] = useState<number[]>([]);
  const [showPoints, setShowPoints] = useState(false);
  const { store, clearRecentlyEarned } = useGrowStore();
  const navigate = useNavigate();

  const streakDays = Array.from({ length: 14 }, (_, i) => i < currentUser.streak);

  // Merge default badges + store-unlocked badges
  const allBadges = [
    ...badges,
    ...Object.entries(STORE_BADGE_MAP)
      .filter(([id]) => store.earnedBadgeIds.includes(id))
      .map(([id, b], i) => ({ id: 100 + i, icon: b.icon, name: b.name, earned: true })),
    // Show locked store badges too
    ...Object.entries(STORE_BADGE_MAP)
      .filter(([id]) => !store.earnedBadgeIds.includes(id))
      .map(([id, b], i) => ({ id: 200 + i, icon: b.icon, name: b.name, earned: false })),
  ];

  return (
    <div className="min-h-screen bg-[#F2F4F6] font-sans">
      <Navbar />

      {/* Earned Toast */}
      {store.recentlyEarned && (
        <EarnedToast
          points={store.recentlyEarned.points}
          badge={store.recentlyEarned.badge}
          onClose={clearRecentlyEarned}
        />
      )}

      <div className="max-w-6xl mx-auto px-4 lg:px-6 pt-24 pb-16 space-y-6">

        {/* ── Greeting ────────────────────────────────────── */}
        <div className="bg-[#191F28] rounded-3xl p-7 lg:p-10 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#00D873]/10 rounded-full translate-x-20 -translate-y-20 pointer-events-none" />
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 relative z-10">
            <div className="flex items-center gap-4">
              <img
                src={currentUser.avatar}
                alt={currentUser.name}
                className="w-16 h-16 rounded-2xl object-cover object-top border-2 border-[#00D873]/30"
              />
              <div>
                <div className="text-white/50 text-sm mb-0.5">오늘 출근(접속)을 환영해요 🎯</div>
                <div className="text-2xl font-black">{currentUser.name} 세오님</div>
                <div className="text-sm text-white/60 mt-0.5">{currentUser.levelIcon} {currentUser.level}</div>
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">🔥</span>
                <span className="font-bold text-sm">N잡 파이프라인 공사 중이에요</span>
                <span className="ml-auto text-[#00D873] font-black">{currentUser.streak}일 연속</span>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {streakDays.map((done, i) => (
                  <div key={i} className={`w-6 h-6 rounded-md flex items-center justify-center text-xs transition-all ${done ? "bg-[#00D873]" : "bg-white/10"}`}>
                    {done && "✓"}
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => setShowPoints(!showPoints)}
              className="bg-white/10 hover:bg-white/20 transition-colors rounded-2xl px-6 py-4 text-center cursor-pointer"
            >
              <div className="text-[#00D873] font-black text-2xl">{store.totalPoints.toLocaleString()}원</div>
              <div className="text-white/50 text-xs mt-0.5">내 시드머니 →</div>
            </button>
          </div>
        </div>

        {/* ── Grow Points Panel (toggle) ─────────────────── */}
        {showPoints && (
          <div className="animate-fade-up">
            <GrowPointsPanel />
          </div>
        )}

        {/* ── Portfolio Shortcut ────────────────────────── */}
        <div className="bg-gradient-to-r from-[#E8F5E9] to-[#F0FFF7] rounded-2xl p-5 flex items-center gap-4 border border-[#00D873]/20">
          <div className="w-10 h-10 rounded-xl bg-[#00D873] flex items-center justify-center flex-shrink-0">
            <i className="ri-user-star-line text-white text-xl" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold text-[#191F28]">내 포트폴리오</p>
            <p className="text-xs text-[#8B95A1] mt-0.5">완료 클래스·배지·수료증을 한 곳에서 공유</p>
          </div>
          <button
            onClick={() => navigate("/portfolio")}
            className="bg-[#00D873] text-white text-xs font-bold px-4 py-2 rounded-full cursor-pointer hover:opacity-90 transition-all whitespace-nowrap flex-shrink-0"
          >
            보기 →
          </button>
        </div>

        {/* ── Completed Classes Banner ─────────────────── */}
        {store.completedClasses.length > 0 && (
          <div className="bg-gradient-to-r from-[#E8F5E9] to-[#F0FFF7] rounded-2xl p-5 flex items-center gap-4 border border-[#00D873]/20">
            <div className="w-10 h-10 rounded-xl bg-[#00D873] flex items-center justify-center flex-shrink-0">
              <i className="ri-graduation-cap-line text-white text-xl" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-[#191F28]">
                {store.completedClasses.length}개 클래스 완료!
              </p>
              <p className="text-xs text-[#8B95A1] mt-0.5 truncate">
                최근: {store.completedClasses[0]?.classTitle}
                {store.completedClasses[0] && ` · 종합 ${store.completedClasses[0].avgScore}점`}
              </p>
            </div>
            <button
              onClick={() => navigate("/explore")}
              className="text-xs font-bold text-[#00D873] whitespace-nowrap cursor-pointer hover:opacity-80 flex-shrink-0"
            >
              더 배우기 →
            </button>
          </div>
        )}

        {/* ── Main Grid ────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Swipe Challenge – left/wide */}
          <div className="lg:col-span-3 bg-white rounded-3xl p-7">
            <SwipeChallengeSection
              completedIds={completedIds}
              onComplete={(id) => setCompletedIds((p) => [...p, id])}
              onSkip={(id) => setSkippedIds((p) => [...p, id])}
            />
          </div>

          {/* Right panel */}
          <div className="lg:col-span-2 space-y-5">
            {/* Party shortcut */}
            <Link to="/party" className="block bg-gradient-to-br from-[#191F28] to-[#2D3748] rounded-3xl p-6 text-white cursor-pointer hover:-translate-y-0.5 transition-transform">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold bg-[#FF6B6B]/20 text-[#FF6B6B] px-3 py-1 rounded-full">파티 챌린지</span>
                <i className="ri-arrow-right-line text-white/40" />
              </div>
              <h3 className="text-lg font-black mb-1">AI로 숏폼 만들기</h3>
              <p className="text-white/50 text-xs mb-4">2자리 남음 · D-2 마감</p>
              <div className="flex -space-x-2">
                {["pm1", "pm2"].map((seq) => (
                  <img key={seq} src={`https://public.readdy.ai/ai/img_res/b4b9cdd06803acfe7baf51b9b8731687.jpg${seq}&orientation=squarish`} alt="" className="w-8 h-8 rounded-full border-2 border-white/20 object-cover object-top" />
                ))}
                {[0, 1].map((i) => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-dashed border-white/20 bg-white/10 flex items-center justify-center">
                    <i className="ri-add-line text-white/40 text-xs" />
                  </div>
                ))}
              </div>
            </Link>

            {/* Badges — includes store-earned badges */}
            <div className="bg-white rounded-3xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-black text-[#191F28]">획득한 배지</h3>
                {store.earnedBadgeIds.some((id) => Object.keys(STORE_BADGE_MAP).includes(id)) && (
                  <span className="text-xs bg-[#00D873]/10 text-[#00D873] font-bold px-2 py-1 rounded-full">
                    신규 🎉
                  </span>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2">
                {allBadges.slice(0, 12).map((b) => (
                  <div
                    key={b.id}
                    title={b.name}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${b.earned ? "bg-[#F2F4F6]" : "bg-[#F9F9F9] opacity-30"}`}
                  >
                    <span className="text-xl">{b.icon}</span>
                    <span className="text-[9px] font-semibold text-[#191F28] text-center leading-tight">{b.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Learning Stats Section ─────────────────────── */}
        <LearningStatsSection />

        {/* ── Weekly Goal ─────────────────────────────────── */}
        <WeeklyGoalSection />

        {/* ── Review Cycle ─────────────────────────────────── */}
        <ReviewCycleSection />

        {/* ── Weakness Analysis ────────────────────────────── */}
        <WeaknessAnalysisSection />

        {/* ── Value Tracker ─────────────────────────────── */}
        <ValueTrackerSection />

        {/* ── ACE Balance Chart ────────────────────────────── */}
        <ACEBalanceSection />

        {/* ── Completed Classes Detail ──────────────────── */}
        {store.completedClasses.length > 0 && (
          <div className="bg-white rounded-3xl p-7">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-black text-[#191F28]">완료한 클래스</h3>
              <button onClick={() => navigate("/explore")} className="text-sm text-[#00D873] font-bold cursor-pointer hover:opacity-80 whitespace-nowrap">
                더 배우기 →
              </button>
            </div>
            <div className="space-y-3">
              {store.completedClasses.map((cls) => (
                <div key={cls.classId} className="flex items-center gap-4 p-4 bg-[#F2F4F6] rounded-2xl">
                  <div className="w-10 h-10 rounded-xl bg-[#00D873]/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-graduation-cap-line text-[#00D873] text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#191F28] truncate">{cls.classTitle}</p>
                    <p className="text-xs text-[#8B95A1] mt-0.5">{cls.completedAt} · 종합 {cls.avgScore}점</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {cls.expertVerified && (
                      <span className="text-xs bg-[#FFF9E6] text-[#FF8C00] font-bold px-2 py-1 rounded-full">
                        👑 전문가
                      </span>
                    )}
                    <span className="text-xs font-extrabold text-[#00D873]">+{cls.pointsEarned}P</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Contribution Graph ────────────────────────── */}
        <div className="bg-white rounded-3xl p-7">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-black text-[#191F28]">챌린지 기여도</h3>
            <span className="text-xs text-[#191F28]/40">최근 12주</span>
          </div>
          <ContributionGraph />
        </div>
      </div>
    </div>
  );
}
